import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Typography,
  Avatar,
  Fade,
  Chip,
  Tooltip,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Send as SendIcon,
  Close as CloseIcon,
  SmartToy as BotIcon,
  Person as PersonIcon,
  Help as HelpIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useChatbot } from '../../context/ChatbotContext';

// Styled components
const ChatContainer = styled(Paper)(({ theme }) => ({
  position: 'fixed',
  bottom: 20,
  right: 20,
  width: 380,
  height: 500,
  display: 'flex',
  flexDirection: 'column',
  zIndex: 1300,
  boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
  borderRadius: 16,
  overflow: 'hidden',
}));

const ChatHeader = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.success.main,
  color: 'white',
  padding: '8px 16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  minHeight: '48px',
}));

const MessageContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: 'auto',
  padding: '16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#c1c1c1',
    borderRadius: '3px',
  },
}));

const MessageBubble = styled(Box)(({ theme, isUser }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '8px',
  flexDirection: isUser ? 'row-reverse' : 'row',
  '& .message-content': {
    maxWidth: '80%',
    padding: '8px 12px',
    borderRadius: isUser ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
    backgroundColor: isUser ? theme.palette.success.main : '#f1f3f4',
    color: isUser ? 'white' : '#333',
    fontSize: '13px',
    lineHeight: '1.3',
    wordWrap: 'break-word',
  },
}));

const InputContainer = styled(Box)(({ theme }) => ({
  padding: '12px',
  borderTop: '1px solid #e0e0e0',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
}));

const SimpleAIChatbot = () => {
  const {
    isOpen: open,
    closeChatbot: onClose,
    messages,
    isTyping,
    showQuickActions,
    sendMessage,
    handleQuickAction,
    clearMessages,
    handleOptionClick,
  } = useChatbot();
  
  const [inputValue, setInputValue] = useState('');
  const [showHelpDialog, setShowHelpDialog] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // No quick actions - simplified chatbot
  const quickActions = [];

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when dialog opens
  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;

    try {
      await sendMessage(inputValue);
      setInputValue('');
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  };

  const handleQuickActionClick = (action) => {
    handleQuickAction(action);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (!open) return null;

  return (
    <>
      <Fade in={open}>
        <ChatContainer elevation={8}>
          {/* Header */}
          <ChatHeader>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
                <BotIcon />
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 600 }}>
                  IMIA
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  IMPRES Intelligent Assistant
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tooltip title="Help">
                <IconButton size="small" onClick={() => setShowHelpDialog(true)} sx={{ color: 'white' }}>
                  <HelpIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Close">
                <IconButton size="small" onClick={onClose} sx={{ color: 'white' }}>
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </ChatHeader>

          {/* Messages */}
          <MessageContainer>
            {/* No quick actions - simplified chatbot */}

            {messages.map((message) => (
              <MessageBubble key={message.id} isUser={message.isUser}>
                <Avatar sx={{ 
                  bgcolor: message.isUser ? 'success.main' : '#f1f3f4',
                  color: message.isUser ? 'white' : '#666',
                  width: 28,
                  height: 28
                }}>
                  {message.isUser ? <PersonIcon /> : (
                    <img 
                      src="http://localhost:3000/static/media/ImpLogo.602f7b19db4d646d5fdb.jpg" 
                      alt="IMIA" 
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  )}
                </Avatar>
                <Box className="message-content">
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-line', fontSize: '13px' }}>
                    {message.text}
                  </Typography>
                  {message.options && message.options.length > 0 && (
                    <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {message.options.map((option, index) => (
                        <Chip
                          key={index}
                          label={option.text}
                          onClick={() => handleOptionClick(option.action, message.id)}
                          size="small"
                          variant="outlined"
                          sx={{ 
                            cursor: 'pointer',
                            '&:hover': { 
                              bgcolor: 'success.light', 
                              color: 'white',
                              borderColor: 'success.main'
                            },
                            transition: 'all 0.2s ease-in-out'
                          }}
                        />
                      ))}
                    </Box>
                  )}
                  <Typography variant="caption" sx={{ 
                    opacity: 0.7, 
                    fontSize: '10px',
                    display: 'block',
                    mt: 0.5
                  }}>
                    {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </Typography>
                </Box>
              </MessageBubble>
            ))}

            {isTyping && (
              <MessageBubble isUser={false}>
                <Avatar sx={{ bgcolor: '#f1f3f4', color: '#666', width: 28, height: 28 }}>
                  <img 
                    src="http://localhost:3000/static/media/ImpLogo.602f7b19db4d646d5fdb.jpg" 
                    alt="IMIA" 
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </Avatar>
                <Box className="message-content">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" sx={{ fontSize: '13px' }}>IMIA waiting...</Typography>
                  </Box>
                </Box>
              </MessageBubble>
            )}
            <div ref={messagesEndRef} />
          </MessageContainer>

          {/* Input */}
          <InputContainer>
            <TextField
              ref={inputRef}
              fullWidth
              placeholder="Type your message..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              variant="outlined"
              size="small"
              multiline
              maxRows={3}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '20px',
                  fontSize: '13px',
                  '& fieldset': {
                    borderColor: '#e0e0e0',
                  },
                  '&:hover fieldset': {
                    borderColor: 'success.main',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'success.main',
                  },
                },
              }}
            />
            <IconButton
              onClick={handleSendMessage}
              disabled={!inputValue.trim() || isTyping}
              sx={{
                bgcolor: 'success.main',
                color: 'white',
                '&:hover': { bgcolor: 'success.dark' },
                '&:disabled': { bgcolor: '#ccc' }
              }}
            >
              <SendIcon />
            </IconButton>
          </InputContainer>
        </ChatContainer>
      </Fade>

      {/* Help Dialog */}
      <Dialog open={showHelpDialog} onClose={() => setShowHelpDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HelpIcon color="success" />
            <Typography variant="h6" sx={{ fontSize: '18px', fontWeight: 'bold' }}>IMIA Help</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: '13px' }}>
            I&apos;m IMIA, your IMPRES Intelligent Assistant. Here&apos;s what I can help you with:
          </Typography>
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1, color: 'success.main', fontSize: '14px', fontWeight: 'bold' }}>
              🔍 Current Features
            </Typography>
            <Box sx={{ pl: 1 }}>
              <Typography variant="body2" sx={{ fontSize: '12px', mb: 0.5 }}>
                <strong>👥 Alumni Chapters:</strong> View all available chapters and their details
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '12px', mb: 0.5 }}>
                <strong>🔍 Alumni Search:</strong> Find alumni by name, batch, course, or organization
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '12px', mb: 0.5 }}>
                <strong>📅 Events:</strong> Get details about upcoming alumni events and meetings
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '12px', mb: 0.5 }}>
                <strong>👶 Children Details:</strong> View your family and children information
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '12px', mb: 0.5 }}>
                <strong>👤 Profile Help:</strong> Guidance for updating your profile information
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '12px', mb: 0.5 }}>
                <strong>❓ General Help:</strong> Answer questions about the platform
              </Typography>
            </Box>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1, color: 'success.main', fontSize: '14px', fontWeight: 'bold' }}>
              💡 How to Use
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px' }}>
              • Click on the option buttons for quick access to features<br/>
              • Type your questions directly in the chat box<br/>
              • Use keywords like &quot;chapters&quot;, &quot;events&quot;, &quot;children&quot;, &quot;profile&quot;<br/>
              • I&apos;ll provide personalized help based on your login status
            </Typography>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1, color: 'success.main', fontSize: '14px', fontWeight: 'bold' }}>
              🔐 Authentication Required
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px' }}>
              Most features require you to be logged in. Please login to access:
              • Chapter details and membership information
              • Alumni search and connections
              • Event information and registration
              • Children and family details
              • Profile management
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ pt: 1 }}>
          <Button 
            onClick={() => setShowHelpDialog(false)}
            variant="contained"
            color="success"
            size="small"
            sx={{ fontSize: '12px' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// PropTypes validation
SimpleAIChatbot.propTypes = {};

export default SimpleAIChatbot;
